import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Spinner } from '../ui/Card';

/**
 * Client-side gate for the admin area. This is a UX convenience only —
 * the real authorization boundary is server-side (requireAdminArea +
 * requirePermission in the Worker, see apps/api/src/middleware/role-guard.ts).
 * Removing or bypassing this component would only change what the UI
 * shows, never what the API allows.
 */
export function RoleGuard({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" state={{ redirectTo: '/admin' }} replace />;
  if (!isAdmin) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="font-display text-xl font-semibold text-ink">Admin Access Only</p>
        <p className="text-sm text-ink/60">This area is restricted to administrators. If you believe this is an error, contact support.</p>
        <a href="/dashboard" className="text-sm font-medium text-seal-blue hover:underline">Go to your Dashboard →</a>
      </div>
    );
  }

  return <>{children}</>;
}
